import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import { i18n } from "../../translate/i18n";

// Backoffice, Etapa D - o backend (Etapa C) e a UNICA fonte de verdade do
// prazo: manda "inicio"/"fim_previsto" (timestamps absolutos) e, quando a
// solicitacao esta "congelado" (completed/cancelled), tambem o
// percentual/tempo_restante ja definitivos, que este componente NUNCA
// recalcula (o relogio parou de verdade, no backend). Quando nao congelado,
// o componente recalcula a exibicao a cada tick local a partir de
// fim_previsto - nao chama a API de novo so para atualizar o contador.
const TICK_MS = 30000;

const useStyles = makeStyles(theme => ({
	wrapper: {
		minWidth: 130,
	},
	barRoot: {
		height: 8,
		borderRadius: 4,
		backgroundColor: theme.palette.grey[300],
	},
	barNormal: {
		backgroundColor: theme.palette.success.main,
	},
	barAtencao: {
		backgroundColor: theme.palette.warning.main,
	},
	barCritico: {
		backgroundColor: theme.palette.error.main,
	},
	barVencido: {
		backgroundColor: theme.palette.error.dark,
	},
	label: {
		fontSize: 11,
		fontWeight: 600,
		marginTop: 3,
		lineHeight: 1.3,
	},
	labelNormal: { color: theme.palette.success.dark },
	labelAtencao: { color: theme.palette.warning.dark },
	labelCritico: { color: theme.palette.error.main },
	labelVencido: { color: theme.palette.error.dark },
}));

const BAR_CLASS_BY_SITUACAO = {
	normal: "barNormal",
	atencao: "barAtencao",
	critico: "barCritico",
	vencido: "barVencido",
};

const LABEL_CLASS_BY_SITUACAO = {
	normal: "labelNormal",
	atencao: "labelAtencao",
	critico: "labelCritico",
	vencido: "labelVencido",
};

// Situacao SEMPRE recalculada localmente a partir do percentual exibido
// (nao so do "situacao" vindo da ultima resposta) - assim a cor/texto
// acompanham o contador mesmo minutos depois da ultima chamada a API, sem
// esperar um novo fetch para "perceber" que passou de uma faixa pra outra.
const situacaoFromPercentual = percentual => {
	if (percentual > 100) return "vencido";
	if (percentual >= 85) return "critico";
	if (percentual >= 60) return "atencao";
	return "normal";
};

const formatDuration = totalSeconds => {
	const abs = Math.abs(Math.round(totalSeconds));
	const days = Math.floor(abs / 86400);
	const hours = Math.floor((abs % 86400) / 3600);
	if (days > 0) return `${days}d ${hours}h`;
	const minutes = Math.floor((abs % 3600) / 60);
	return `${hours}h ${minutes}min`;
};

// sla = bloco vindo da API (GET /solicitacoes, GET /solicitacoes/:id etc.):
// { prazo_horas, inicio, fim_previsto, percentual_consumido,
//   tempo_restante_segundos, situacao, congelado }
const SlaProgress = ({ sla }) => {
	const classes = useStyles();
	// Só serve para forçar o re-render a cada tick - o valor em si não é
	// usado no cálculo (que sempre lê Date.now() no momento do render).
	const [, setTick] = useState(0);

	useEffect(() => {
		if (!sla || sla.congelado) return undefined;
		const interval = setInterval(() => setTick(t => t + 1), TICK_MS);
		return () => clearInterval(interval);
	}, [sla]);

	if (!sla) return null;

	const inicioMs = new Date(sla.inicio).getTime();
	const fimMs = new Date(sla.fim_previsto).getTime();
	const totalMs = fimMs - inicioMs;

	const tempoRestanteSegundos = sla.congelado
		? sla.tempo_restante_segundos
		: Math.round((fimMs - Date.now()) / 1000);

	const percentual = sla.congelado
		? sla.percentual_consumido
		: totalMs > 0
		? Math.round(((Date.now() - inicioMs) / totalMs) * 100)
		: 100;

	const situacao = situacaoFromPercentual(percentual);
	const vencido = tempoRestanteSegundos < 0;
	const barClass = classes[BAR_CLASS_BY_SITUACAO[situacao]];
	const labelClass = classes[LABEL_CLASS_BY_SITUACAO[situacao]];

	const duracaoTexto = formatDuration(tempoRestanteSegundos);
	const texto = vencido
		? `${i18n.t("minhasSolicitacoes.sla.vencido")} · +${duracaoTexto}`
		: `${percentual}% · ${duracaoTexto} ${i18n.t("minhasSolicitacoes.sla.restantes")}`;

	const tooltipTexto = `${i18n.t("minhasSolicitacoes.sla.prazo")}: ${sla.prazo_horas}h`;

	return (
		<Tooltip title={tooltipTexto}>
			<div className={classes.wrapper}>
				<LinearProgress
					variant="determinate"
					value={Math.min(Math.max(percentual, 0), 100)}
					classes={{ root: classes.barRoot, bar: barClass }}
				/>
				<Typography className={`${classes.label} ${labelClass}`}>{texto}</Typography>
			</div>
		</Tooltip>
	);
};

export default SlaProgress;
